
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { CheckCircle, XCircle, ChevronDown } from "lucide-react";
import { continents } from "@/utils/supportedCountries";
import { SEO } from "@/components/SEO";
import { useIsMobile } from "@/hooks/use-mobile";
import { useState } from "react";

const SupportedCountries = () => {
  const isMobile = useIsMobile();
  const [openStates, setOpenStates] = useState<Record<string, boolean>>({});

  const toggleOpen = (continentName: string) => {
    setOpenStates(prev => ({
      ...prev,
      [continentName]: !prev[continentName]
    }));
  };

  const getCardBackground = (continentName: string) => {
    if (continentName === "Europe" || continentName === "North America") {
      return "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700";
    }
    return "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700";
  };

  const getTextColor = (continentName: string) => {
    if (continentName === "Europe" || continentName === "North America") {
      return "text-green-900 dark:text-green-100";
    }
    return "text-red-900 dark:text-red-100";
  };

  const renderCountryCard = (continent: any) => {
    const cardBg = getCardBackground(continent.name);
    const textColor = getTextColor(continent.name);

    if (isMobile) {
      return (
        <Collapsible 
          key={continent.name}
          open={openStates[continent.name] || false}
          onOpenChange={() => toggleOpen(continent.name)}
        >
          <Card className={`${cardBg}`}>
            <CollapsibleTrigger className="w-full">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className={`text-xl font-semibold ${textColor}`}>
                    {continent.name}
                  </CardTitle>
                  <ChevronDown className={`w-5 h-5 transition-transform ${openStates[continent.name] ? 'rotate-180' : ''} ${textColor}`} />
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-3">
                {continent.countries.map((country: any) => (
                  <div key={country.code} className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{country.flag}</span>
                      <span className={`text-sm font-medium ${textColor}`}>
                        {country.name}
                      </span>
                    </div>
                    <Tooltip>
                      <TooltipTrigger>
                        {country.supported ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-gray-400" />
                        )}
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{country.supported ? "Active" : "Not Active"}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                ))}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      );
    }

    return (
      <Card key={continent.name} className={cardBg}>
        <CardHeader className="pb-4">
          <CardTitle className={`text-xl font-semibold text-center ${textColor}`}>
            {continent.name}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {continent.countries.map((country: any) => (
            <div key={country.code} className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{country.flag}</span>
                <span className={`text-sm font-medium ${textColor}`}>
                  {country.name}
                </span>
              </div>
              <Tooltip>
                <TooltipTrigger>
                  {country.supported ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <XCircle className="w-5 h-5 text-gray-400" />
                  )}
                </TooltipTrigger>
                <TooltipContent>
                  <p>{country.supported ? "Active" : "Not Active"}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Supported Countries - blankpoll"
        description="Discover which countries are supported for our reward programs. Currently supporting all European Union member states."
        type="website"
        name="blankpoll"
        imageUrl="/og-image.png"
      />
      <Navigation />
      
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-secondary dark:text-white mb-4">
              Supported Countries
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Below is a comprehensive list of all supported countries for our reward programs. 
              Please note that even if you earn rewards, you must be a citizen of one of the active countries to participate in our reward programs. 
              If you are not from a supported country, you can still earn bp points and badges.
            </p>
          </div>

          {/* Countries Grid */}
          <TooltipProvider>
            {isMobile ? (
              <div className="grid grid-cols-1 gap-6 mb-8">
                {continents.map(renderCountryCard)}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-6 mb-8">
                  {continents.slice(0, 4).map(renderCountryCard)}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  {continents.slice(4).map(renderCountryCard)}
                </div>
              </>
            )}
          </TooltipProvider>

          {/* Info Section */}
          <div className="mt-12">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2 text-center">
                Expansion Plans
              </h3>
              <p className="text-blue-700 dark:text-blue-200 text-center">
                We're actively working to expand our reward programs to more countries. 
                Stay tuned for updates as we add support for additional regions worldwide.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SupportedCountries;
